import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid } from '@mui/material';
import Swal from 'sweetalert2';

import { SCUIContent } from '../../atomic/organisms';
import { SCUIBreadCrumb } from '../../atomic/molecules';
import { EstimatedDateGrid, EstimatedDateSummaryTable } from './components';
import { SCUILoadingLinear } from '../../atomic/atoms';

import { calculateAddedDate } from './helper/EstimatedShippingDate.helper';
import CargoEstimationCommand from '../../services/command/cargoEstimation/CargoEstimationCommand.services';
import TimeHelper from '../../utils/time';
import { RowData } from './helper/EstimatedShippingDate.types';
import { ESD } from './estimatedShippingDate.classes';

const startDate = calculateAddedDate(new Date(), 1);
const today = new Date();
const generatedDates = Array.from(
    { length: 14 },
    (_, i) => calculateAddedDate(startDate, i).toISOString().split('T')[0]
);

export const EstimatedShippingDate = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [gridData, setGridData] = useState<any[]>([]);
    const [defaultGridData, setDefaultGridData] = useState<any[]>([]);
    const [updatedData, setUpdatedData] = useState<any[]>([]);
    const [validationError, setValidationError] = useState<boolean>(false);
    const [estimatedShippedDayFromBackoffice, setEstimatedShippedDayFromBackoffice] = useState<string | number>('');
    const [estimatedShippedDay, setEstimatedShippedDay] = useState<string | number>('');
    const [backlog, setBacklog] = useState<number>(0);

    const { t } = useTranslation();

    const handleUpdateRow = (updatedRow: any) => {
        setGridData((prev) => prev.map((row) => (row.date === updatedRow.date ? updatedRow : row)));
    };

    const hasSevenConsecutiveDays = (data: any) => {
        const sortedDates = data
            .filter(
                (row: any) =>
                    row.salesForecast !== '' &&
                    row.warehouseExitForecast !== '' &&
                    row.salesForecast !== undefined &&
                    row.salesForecast !== null &&
                    row.warehouseExitForecast !== undefined &&
                    row.warehouseExitForecast !== null
            )
            .map((row: any) => new Date(row.date).getTime())
            .sort((a: any, b: any) => a - b);

        let maxConsecutiveCount = 1;
        let currentConsecutiveCount = 1;

        for (let i = 1; i < sortedDates.length; i++) {
            const isNextDay = sortedDates[i] - sortedDates[i - 1] === 24 * 60 * 60 * 1000;

            if (isNextDay) {
                currentConsecutiveCount += 1;
                maxConsecutiveCount = Math.max(maxConsecutiveCount, currentConsecutiveCount);
            } else {
                currentConsecutiveCount = 1; // Reset the count when the streak is broken
            }

            if (maxConsecutiveCount >= 7) {
                return true; // Early exit if a series of 7 consecutive days is found
            }
        }

        return false; // Return false if no series of 7 consecutive days is found
    };

    // Helper function to check if a row has changed
    const hasRowChanged = (updatedRow: RowData, originalRow: any) => {
        if (!originalRow) return true;

        const isSalesForecastChanged =
            (updatedRow.salesForecast === '0' && originalRow.salesForecast === '') ||
            (updatedRow.salesForecast === '' && originalRow.salesForecast === '0')
                ? true
                : Number(updatedRow.salesForecast) !== Number(originalRow.salesForecast);

        const isWarehouseExitForecastChanged =
            (updatedRow.warehouseExitForecast === '0' && originalRow.warehouseExitForecast === '') ||
            (updatedRow.warehouseExitForecast === '' && originalRow.warehouseExitForecast === '0')
                ? true
                : Number(updatedRow.warehouseExitForecast) !== Number(originalRow.warehouseExitForecast);

        return isSalesForecastChanged || isWarehouseExitForecastChanged;
    };

    // Helper function to get changed rows
    const getChangedRows = () => {
        return Object.values(updatedData).filter((updatedRow: RowData) => {
            const originalRow = defaultGridData?.find(
                (row) => TimeHelper.formatDate(row.date) === TimeHelper.formatDate(updatedRow.date)
            );
            return hasRowChanged(updatedRow, originalRow);
        });
    };

    // Helper function to transform rows to estimations
    const transformToEstimations = (rows: RowData[]) => {
        return rows?.map((row: RowData) => ({
            date: TimeHelper.formatDateDesc(row.date),
            lcwSalesForecast: row.salesForecast === '' ? null : Number(row.salesForecast),
            lcwWarehouseExitForecast: row.warehouseExitForecast === '' ? null : Number(row.warehouseExitForecast),
        }));
    };

    // Helper function to save estimations
    const saveEstimations = async (estimations: any[]) => {
        if (estimations?.length !== 0) {
            await CargoEstimationCommand.PostEstimations({
                forecastModels: estimations,
            });
            Swal.fire(t('general.updated'), ' ', 'success');
            await fetchEstimationData();
        } else {
            Swal.fire(t('general.updated'), '', 'success');
        }
    };

    // Helper function to process the save operation
    const processSaveOperation = async () => {
        if (!hasSevenConsecutiveDays(gridData)) {
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('cargoManagement.estimatedShippingDate.noSevenConsecutiveDays'),
            });
            setIsLoading(false);
            return;
        }

        const changedRows = getChangedRows();
        const estimations = transformToEstimations(changedRows);

        setIsLoading(true);

        try {
            await saveEstimations(estimations);
        } catch (err: any) {
            Swal.fire(t('general.error'), err.toString() || '', 'error');
            setIsLoading(false);
        }
    };

    const handleSaveEstimations = () => {
        Swal.fire({
            text: t('general.sureText'),
            cancelButtonText: t('general.cancel'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('general.save'),
        }).then(() => {
            processSaveOperation();
        });
    };

    const handleSaveBacklog = async () => {
        Swal.fire({
            text: t('general.sureText'),
            cancelButtonText: t('general.cancel'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('general.save'),
        }).then(() => {
            CargoEstimationCommand.PostCargoBacklogDay({
                date: TimeHelper.formatDateDesc(today),
                estimatedShippedDayFromBackoffice: +estimatedShippedDayFromBackoffice || +estimatedShippedDay || null,
            })
                .then(async () => {
                    Swal.fire(t('general.updated'), ' ', 'success');
                    const res = await CargoEstimationCommand.GetEstimationList({
                        startDate: TimeHelper.formatDateDesc(today),
                        endDate: TimeHelper.formatDateDesc(today),
                    });
                    setEstimatedShippedDay(res?.estimatedShippedHoursList?.[0]?.estimatedShippedDay);
                    setBacklog(res?.estimatedShippedHoursList?.[0]?.backlog || 0);
                    setEstimatedShippedDayFromBackoffice(
                        res?.estimatedShippedHoursList?.[0]?.estimatedShippedDayFromBackoffice
                    );
                })
                .catch((err: any) => {
                    Swal.fire(t('general.error'), err.toString() || '', 'error');
                    setIsLoading(false);
                });
        });
    };

    // API does not provide 14 days of data, unless there are any pre posted values
    // This part generates empty rowValues for the missing days to provide inputs

    const fetchEstimationData = async () => {
        try {
            const res = await CargoEstimationCommand.GetEstimationList({
                startDate: TimeHelper.formatDateDesc(today),
                endDate: TimeHelper.formatDateDesc(calculateAddedDate(startDate, 13)),
            });

            const fullData = res?.estimatedShippedHoursList || [];

            const dataMap: any = new Map(
                fullData?.map((item: any) => [
                    TimeHelper.formatDateDesc(item.date),
                    {
                        salesForecast: item.salesForecast === 0 ? 0 : item.salesForecast || '',
                        warehouseExitForecast: item.warehouseExitForecast === 0 ? 0 : item.warehouseExitForecast || '',
                        updateCount: item.updateCount,
                    },
                ])
            );

            const fullGridData = generatedDates?.map((date) => ({
                date,
                salesForecast: dataMap?.get(date)?.salesForecast,
                warehouseExitForecast: dataMap?.get(date)?.warehouseExitForecast,
                isDisabled: dataMap?.get(date)?.updateCount > 50,
            }));

            setGridData(fullGridData);
            setDefaultGridData(fullGridData);
            setEstimatedShippedDay(fullData?.[0]?.estimatedShippedDay);
            setBacklog(fullData[0]?.backlog || 0);
            setEstimatedShippedDayFromBackoffice(fullData[0]?.estimatedShippedDayFromBackoffice);
        } catch (err: any) {
            Swal.fire(t('general.error'), err.toString() || '', 'error');
            setIsLoading(false);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchEstimationData();
    }, []);

    return (
        <SCUIContent>
            <Box className={ESD.page}>
            {isLoading ? (
                <Grid
                    className={ESD.loadingWrap}
                    size={{
                        xl: 12,
                        md: 12,
                        lg: 12,
                        xs: 12
                    }}>
                    <SCUILoadingLinear display={isLoading} />
                </Grid>
            ) : (
                <></>
            )}
            <SCUIBreadCrumb
                title={t('cargoManagement.estimatedShippingDate.title')}
                componentLink={'/estimated-shipping-date'}
                style={{ cursor: 'pointer' }}
            />
            <Grid container spacing={4} className={ESD.mainGrid} sx={{ marginTop: '16px' }}>
                <Grid
                    className={ESD.summaryWrap}
                    sx={{ margin: '16px auto' }}
                    size={{
                        xs: 12,
                        sm: 12,
                        md: 8,
                        lg: 8
                    }}>
                    <EstimatedDateSummaryTable
                        date={today}
                        backlog={backlog}
                        estimatedShippedDay={estimatedShippedDay}
                        estimatedShippedDayFromBackoffice={estimatedShippedDayFromBackoffice}
                        onUpdateSummaryInput={(_, value) => setEstimatedShippedDayFromBackoffice(value)}
                        onSaveBacklog={() => handleSaveBacklog()}
                        onSaveEstimations={() => {
                            if (!validationError) {
                                handleSaveEstimations();
                            }
                        }}
                        isEstimationsSaveDisabled={
                            validationError ||
                            isLoading ||
                            !gridData?.every(
                                (item: any) => item.salesForecast === '' || Number(item.salesForecast) !== 0
                            )
                        }
                        isLoading={isLoading}
                    />
                </Grid>
                <Grid
                    className={ESD.forecastGridWrap}
                    size={{
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 12
                    }}>
                    <EstimatedDateGrid
                        data={gridData || []}
                        backlog={backlog}
                        onUpdateRow={handleUpdateRow}
                        setUpdatedData={setUpdatedData}
                        defaultDate={startDate}
                        onGridError={setValidationError}
                    />
                </Grid>
            </Grid>
            </Box>
        </SCUIContent>
    );
};
